import { useState } from "react";
import { Plus, Check, Sparkles } from "lucide-react";
import { FeatureShell, DemoPanel } from "../components/sections/FeatureShell";

const START = [
  { n: "Chicken breasts", a: "Meat & Fish", s: "have" }, { n: "Zucchini", a: "Produce", s: "have" }, { n: "Bell peppers", a: "Produce", s: "low" },
  { n: "Garlic", a: "Produce", s: "have" }, { n: "Olive oil", a: "Pantry", s: "low" }, { n: "Brown rice", a: "Pantry", s: "have" },
  { n: "Salmon fillets", a: "Meat & Fish", s: "out" }, { n: "Honey", a: "Pantry", s: "out" }, { n: "Greek yogurt", a: "Dairy", s: "have" }, { n: "Lemons", a: "Produce", s: "out" },
];
const STATES = { have: ["In stock", "bg-emerald-400/15 text-emerald-200 border-emerald-300/40"], low: ["Running low", "bg-amber-400/15 text-amber-200 border-amber-300/40"], out: ["Out", "bg-rose-400/15 text-rose-200 border-rose-300/40"] };
const NEXT = { have: "low", low: "out", out: "have" };

export default function Pantry() {
  const [items, setItems] = useState(START);
  const [extra, setExtra] = useState([]);
  const [draft, setDraft] = useState("");
  const [bought, setBought] = useState([]);

  const list = [...items.filter((x) => x.s !== "have").map((x) => ({ n: x.n, a: x.a })), ...extra];
  const aisles = [...new Set(list.map((x) => x.a))];
  const have = items.filter((x) => x.s === "have").map((x) => x.n);

  return (
    <FeatureShell slug="pantry-shopping">
      <DemoPanel title="Sample pantry & smart list" note="Demonstration only. Sample pantry data — tap items to change their status. Nothing is saved.">
        <div className="grid gap-8 lg:grid-cols-2">
          <div>
            <p className="eyebrow mb-4">Your pantry — tap to update</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {items.map((x, i) => (
                <button key={x.n} onClick={() => setItems((l) => l.map((y, j) => (j === i ? { ...y, s: NEXT[y.s] } : y)))} data-testid={`pantry-item-${i}`}
                  className="flex items-center justify-between rounded-xl border border-cyan-400/20 bg-deep/60 px-4 py-3 text-left transition-colors hover:border-cyan-300/60">
                  <span className="text-sm text-white">{x.n}</span>
                  <span className={`rounded-full border px-2.5 py-0.5 text-xs ${STATES[x.s][1]}`}>{STATES[x.s][0]}</span>
                </button>
              ))}
            </div>
            <div className="mt-6 rounded-2xl bg-blue-500/10 p-5" data-testid="use-it-up">
              <p className="flex items-center gap-2 text-sm font-semibold text-white"><Sparkles className="h-4 w-4 text-cyan-300" />Use it up tonight (sample suggestion)</p>
              <p className="mt-2 text-sm text-slate-300">{have.length >= 3 ? `With ${have.slice(0, 3).join(", ").toLowerCase()} on hand, a garlic chicken & vegetable rice bowl needs nothing extra.` : "Mark a few items as in stock to see a sample suggestion."}</p>
            </div>
          </div>
          <div className="rounded-3xl border border-cyan-300/30 bg-gradient-to-br from-blue-950/70 to-deep p-6" data-testid="shopping-list">
            <p className="eyebrow">Shopping list · {list.length} items</p>
            <form onSubmit={(e) => { e.preventDefault(); if (draft.trim()) { setExtra((l) => [...l, { n: draft.trim(), a: "Added by you" }]); setDraft(""); } }} className="mt-4 flex gap-2">
              <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Add an item" data-testid="shopping-add-input" className="flex-1 rounded-xl border border-cyan-400/25 bg-deep/70 px-4 py-2.5 text-white placeholder:text-slate-400 focus:border-cyan-300 focus:outline-none" />
              <button data-testid="shopping-add-btn" className="btn-primary !px-4 !py-2.5" aria-label="Add item"><Plus className="h-4 w-4" /></button>
            </form>
            <div className="mt-6 space-y-6">
              {aisles.map((a) => (
                <div key={a}>
                  <p className="mb-2 font-mono text-xs uppercase tracking-[0.2em] text-cyan-300">{a}</p>
                  {list.filter((x) => x.a === a).map((x) => {
                    const done = bought.includes(x.n);
                    return (
                      <button key={x.n} onClick={() => setBought((b) => (done ? b.filter((y) => y !== x.n) : [...b, x.n]))} data-testid={`shopping-item-${x.n.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex w-full items-center gap-3 border-b border-white/5 py-2.5 text-left">
                        <span className={`grid h-5 w-5 place-items-center rounded-md border ${done ? "border-cyan-300 bg-cyan-400 text-ink" : "border-cyan-400/40"}`}>{done && <Check className="h-3.5 w-3.5" />}</span>
                        <span className={`text-sm ${done ? "text-slate-400 line-through" : "text-white"}`}>{x.n}</span>
                      </button>
                    );
                  })}
                </div>
              ))}
              {!list.length && <p className="text-sm text-slate-300">Everything’s stocked. Nice.</p>}
            </div>
          </div>
        </div>
      </DemoPanel>
    </FeatureShell>
  );
}
